import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import BackButton from "@/components/back-button";
import { captureEvent } from "@/lib/analytics/posthog";
import type { EventFilter } from "@/queries/api/dashboardV2Api";
import { EventFilters } from "@/queries/api/dashboardV2Api";
import { useInfiniteEventsQuery } from "@/queries/hooks/rosterHooks";

import { EventRow } from "../dashboard/-components/v2/activity-timeline";

export const Route = createFileRoute("/app/$role/_app/activity/")({
  component: ActivityPage,
});

function ActivityPage() {
  const { t } = useTranslation("dashboard");
  const [filter, setFilter] = useState<EventFilter>("all");
  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteEventsQuery(filter);

  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const filterLabels: Record<EventFilter, string> = {
    all: t("v2.activity.filters.all"),
    signup: t("v2.activity.filters.signup"),
    confirmed: t("v2.activity.filters.confirmed"),
    cancellation: t("v2.activity.filters.cancellation"),
    posted: t("v2.activity.filters.posted"),
    completed: t("v2.activity.filters.completed"),
  };

  return (
    <div className="flex flex-col w-full px-3 sm:px-6 md:px-8 pt-4 sm:pt-6 md:pt-8 pb-16 max-w-[820px] mx-auto">
      <BackButton />
      <div className="flex items-center justify-between gap-3 mt-4 mb-4 flex-wrap">
        <h1 className="heading-1">{t("v2.activity.title")}</h1>
        <select
          aria-label={t("v2.activity.filterLabel")}
          value={filter}
          onChange={(e) => {
            const next = e.target.value as EventFilter;
            setFilter(next);
            captureEvent("activity_filter_changed", { filter: next });
          }}
          className="h-9 rounded-lg border border-fog-dark bg-white text-sm text-slate px-2"
        >
          {EventFilters.map((f) => (
            <option key={f} value={f}>{filterLabels[f]}</option>
          ))}
        </select>
      </div>

      <section className="bg-white rounded-[15px] p-4 sm:p-5">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-12 rounded-lg bg-fog animate-pulse" />
            ))}
          </div>
        ) : isError ? (
          <div className="flex items-center gap-3 text-sm text-slate">
            {t("v2.errorSection")}
            <button type="button" onClick={() => refetch()} className="underline text-teal">
              {t("v2.retry")}
            </button>
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate py-8 text-center">{t("v2.activity.empty")}</p>
        ) : (
          <>
            <ul className="flex flex-col divide-y divide-[#545A63]/15">
              {items.map((e) => (
                <EventRow key={e.id} event={e} />
              ))}
            </ul>
            <div className="pt-4 flex justify-center">
              {hasNextPage ? (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="h-10 px-5 rounded-lg border border-fog-dark text-sm text-garnet hover:bg-fog disabled:opacity-50"
                >
                  {isFetchingNextPage ? t("v2.loading") : t("v2.loadMore")}
                </button>
              ) : (
                <span className="text-xs text-slate">{t("v2.activity.end")}</span>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
